import React from "react";
import { Button } from "@zendeskgarden/react-buttons";
import { differenceInMinutes } from "date-fns";
import * as stylex from "@stylexjs/stylex";
import { useMutation } from "urql";
import { generateCodeChallenge, generateCodeVerifier } from "@linear/common/auth/pkce";
import { oauthAuthStringChallengeMutation, oauthAuthStringCheckMutation, type OauthAuthStringChallengePayload, type OauthAuthStringCheckPayload } from "~/queries";
import { Config } from "../config";
import "../styles/variables.stylex";
import { ZendeskThemeVariables } from "../styles/variables.stylex";
const OAUTH_SCOPE = ["read", "write"];
const authUrl = `${Config.OAUTH_URL}?client_id=${Config.OAUTH_CLIENT_ID}&response_type=stringauth&redirect_uri=${Config.OAUTH_REDIRECT_URI}&scope=${OAUTH_SCOPE.join(",")}`;
type Props = {
  /**
   * Runs after OAuth succeeds so the app can persist the received access token.
   *
   * @param accessToken The OAuth access token returned by a successful login.
   */
  onSuccess(accessToken: string): void;
};

/**
 * Starts the Zendesk-friendly OAuth flow that signs the current agent into Linear.
 *
 * Use this in the unauthenticated branch of {@link Root} ; successful polling passes the token to onSuccess.
 *
 * @param props The component properties described by {@link Props} .
 * @returns The rendered content.
 */
export function LoginButton({
  onSuccess
}: Props) {
  const [authString, setAuthString] = React.useState<string>();
  const [verifier, setVerifier] = React.useState<string>();
  const [, challengeMutation] = useMutation<OauthAuthStringChallengePayload>(oauthAuthStringChallengeMutation);
  const handleLogin = async () => {
    // Generate code verifier and challenge on the client (PKCE)
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const challengeMutationResult = await challengeMutation({
      appId: Config.OAUTH_CLIENT_ID,
      scope: OAUTH_SCOPE,
      codeChallenge
    });
    setAuthString(challengeMutationResult.data?.oauthAuthStringChallenge.authString);
    setVerifier(codeVerifier);
  };
  const handleReset = () => {
    setAuthString(undefined);
    setVerifier(undefined);
  };
  return <div>
      {authString && verifier ? <AuthString authString={authString} verifier={verifier} onSuccess={onSuccess} onReset={handleReset} /> : <Button isStretched onClick={handleLogin}>
          Log in with Linear
        </Button>}
    </div>;
}
const AuthString = (props: {
  authString: string;
  verifier: string;
  onSuccess(accessToken: string): void;
  onReset(): void;
}) => {
  const {
    authString,
    verifier,
    onSuccess,
    onReset
  } = props;
  const [startedPollingAt] = React.useState<Date>(new Date());
  const [, checkMutation] = useMutation<OauthAuthStringCheckPayload>(oauthAuthStringCheckMutation);
  const poll = async () => {
    // Stop polling and reset state after the token expires anyway.
    if (differenceInMinutes(new Date(), startedPollingAt) >= 5) {
      onReset();
      return;
    }
    const checkMutationResult = await checkMutation({
      appId: Config.OAUTH_CLIENT_ID,
      authString,
      codeVerifier: verifier
    });
    if (checkMutationResult.data?.oauthAuthStringCheck.success && checkMutationResult.data?.oauthAuthStringCheck.token) {
      onSuccess(checkMutationResult.data?.oauthAuthStringCheck.token);
      return;
    }
  };
  React.useEffect(() => {
    if (!authString) {
      return;
    }

    // Open the oAuth flow screen and provide the auth string.
    window.open(authUrl + `&authstring=${authString}`, "authWindow", `height=${600},width=${500},top=${screen.height / 2 - 300},left=${screen.width / 2 - 250}`);
    const interval = setInterval(poll, 3000);
    return () => {
      clearInterval(interval);
    };
  }, []);
  return <div className="sx-18mflkt sx-nxx81d sx-fifm61 sx-8qomae sx-2b8uid">
      <h2 className="sx-1s688f sx-yi6m4r sx-if65rj sx-17e5z5g">Waiting for log in…</h2>
      We've opened Linear in another window. <br />
      Return here when you've completed authorization.
      <Button isBasic isStretched onClick={onReset}>
        Cancel
      </Button>
    </div>;
};